import { useState, useEffect } from 'react';

/**
 * Reusable Date Filter Component for monthly/yearly filtering
 * 
 * Usage:
 * <DateFilter onFilterChange={(filters) => setFilters(filters)} />
 * 
 * The onFilterChange callback receives: { startDate, endDate, period, month, year }
 */
export default function DateFilter({ onFilterChange, showPeriodFilter = true, className = '' }) {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const [period, setPeriod] = useState('all'); // 'all', 'today', 'week', 'month', 'quarter', 'year', 'custom'
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Generate years array (last 5 years + next year)
  const years = [];
  for (let y = currentYear - 4; y <= currentYear + 1; y++) {
    years.push(y);
  }

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const quarters = [
    { value: 'Q1', label: 'Q1 (Jan-Mar)', months: [1, 2, 3] },
    { value: 'Q2', label: 'Q2 (Apr-Jun)', months: [4, 5, 6] },
    { value: 'Q3', label: 'Q3 (Jul-Sep)', months: [7, 8, 9] },
    { value: 'Q4', label: 'Q4 (Oct-Dec)', months: [10, 11, 12] },
  ];

  // Format date to YYYY-MM-DD in LOCAL timezone (not UTC)
  const formatDateISO = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    let startDate = null;
    let endDate = null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (period) {
      case 'today':
        startDate = formatDateISO(today);
        endDate = formatDateISO(today);
        break;

      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        startDate = formatDateISO(weekStart);
        endDate = formatDateISO(weekEnd);
        break;

      case 'month':
        const monthStart = new Date(selectedYear, selectedMonth - 1, 1);
        const monthEnd = new Date(selectedYear, selectedMonth, 0);
        startDate = formatDateISO(monthStart);
        endDate = formatDateISO(monthEnd);
        break;

      case 'quarter':
        const q = Math.ceil(selectedMonth / 3);
        const quarterMonths = quarters[q - 1].months;
        const quarterStart = new Date(selectedYear, quarterMonths[0] - 1, 1);
        const quarterEnd = new Date(selectedYear, quarterMonths[2], 0);
        startDate = formatDateISO(quarterStart);
        endDate = formatDateISO(quarterEnd);
        break;

      case 'year':
        startDate = `${selectedYear}-01-01`;
        endDate = `${selectedYear}-12-31`;
        break;

      case 'custom':
        startDate = customStartDate || null;
        endDate = customEndDate || null;
        break;

      case 'all':
      default:
        startDate = null;
        endDate = null;
        break;
    }

    onFilterChange({
      startDate,
      endDate,
      period,
      month: selectedMonth,
      year: selectedYear
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, selectedMonth, selectedYear, customStartDate, customEndDate]);

  const handleQuickFilter = (newPeriod) => {
    setPeriod(newPeriod);
  };

  return (
    <div className={`bg-white rounded-lg shadow p-4 ${className}`}>
      <div className="flex flex-wrap items-center gap-4">
        {/* Quick Filters */}
        {showPeriodFilter && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleQuickFilter('all')}
              className={`px-3 py-1.5 text-sm rounded-lg transition ${
                period === 'all' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Time
            </button>
            <button
              onClick={() => handleQuickFilter('today')}
              className={`px-3 py-1.5 text-sm rounded-lg transition ${
                period === 'today' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => handleQuickFilter('week')}
              className={`px-3 py-1.5 text-sm rounded-lg transition ${
                period === 'week' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              This Week
            </button>
            <button
              onClick={() => handleQuickFilter('month')}
              className={`px-3 py-1.5 text-sm rounded-lg transition ${
                period === 'month' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => handleQuickFilter('quarter')}
              className={`px-3 py-1.5 text-sm rounded-lg transition ${
                period === 'quarter' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Quarterly
            </button>
            <button
              onClick={() => handleQuickFilter('year')}
              className={`px-3 py-1.5 text-sm rounded-lg transition ${
                period === 'year' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Yearly
            </button>
            <button
              onClick={() => handleQuickFilter('custom')}
              className={`px-3 py-1.5 text-sm rounded-lg transition ${
                period === 'custom' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Custom
            </button>
          </div>
        )}

        {/* Month/Year Selectors - shown for month, quarter, year */}
        {(period === 'month' || period === 'quarter' || period === 'year') && (
          <div className="flex gap-2 items-center">
            {period === 'month' && (
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                {months.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            )}
            {period === 'quarter' && (
              <select
                value={Math.ceil(selectedMonth / 3)}
                onChange={(e) => {
                  const q = parseInt(e.target.value);
                  setSelectedMonth(quarters[q - 1].months[0]);
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                {quarters.map((q, idx) => (
                  <option key={q.value} value={idx + 1}>{q.label}</option>
                ))}
              </select>
            )}
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        )}

        {/* Custom Date Range */}
        {period === 'custom' && (
          <div className="flex gap-2 items-center">
            <label className="text-sm text-gray-600">From:</label>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            />
            <label className="text-sm text-gray-600">To:</label>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Helper function to filter data by date range
 * @param {Array} data - Array of objects with date field
 * @param {string} dateField - Name of the date field in objects (e.g., 'po_date', 'challan_date')
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Array} Filtered data
 */
export const filterByDateRange = (data, dateField, startDate, endDate) => {
  if (!startDate && !endDate) return data;
  
  return data.filter(item => {
    const itemDate = item[dateField];
    if (!itemDate) return false;
    
    // Handle different date formats
    let dateStr = itemDate;
    if (itemDate.includes('T')) {
      dateStr = itemDate.split('T')[0];
    }
    
    if (startDate && dateStr < startDate) return false;
    if (endDate && dateStr > endDate) return false;
    
    return true;
  });
};
